import { createContext, useContext, useEffect, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SupportedLanguage } from '@/types';

interface LanguageContextType {
    language: SupportedLanguage;
    setLanguage: (lang: SupportedLanguage) => void;
    t: (key: string, options?: any) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
    const { t, i18n } = useTranslation();
    const [language, setLanguageState] = useState<SupportedLanguage>(i18n.language as SupportedLanguage || 'en');

    const setLanguage = (lang: SupportedLanguage) => {
        i18n.changeLanguage(lang);
        setLanguageState(lang);
    };

    useEffect(() => {
        if (i18n.language !== language) {
            setLanguageState(i18n.language as SupportedLanguage);
        }
    }, [i18n.language]);

    return (
        <LanguageContext.Provider value={{
            language,
            setLanguage,
            t: (key: string, options?: any) => t(key, options) as string
        }}>
            {children}
        </LanguageContext.Provider>
    );
};

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};
